import { LAYOUT_CONSTANTS } from "@src/constants";
import { useCanvasContext } from "@src/context";
import { useCanvasPortalScrollFix } from "@src/hooks/useCanvasPortalScrollFix";
import type { DepartmentOption } from "@src/utils/department-options-utils";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Text } from "@uhg-abyss/web/ui/Text";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

const SlotDetails = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  marginLeft: "-16px",
  marginRight: "-16px",
  width: "calc(100% + 32px)",
  padding: "12px 16px",
  backgroundColor: "$app.color.location.selection.modal.slot.detail.background",
});

const ProviderNameContainer = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const formatAppointmentTime = (time24h: string): string => {
  const [hoursRaw, minutesRaw] = time24h.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return time24h;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
};

const formatSlotDateTime = (appointmentDate?: string, startTime?: string): string => {
  if (!appointmentDate || !startTime) {
    return "";
  }

  // appointmentDate is in ISO format (YYYY-MM-DD)
  const date = dayjs(appointmentDate);

  // Verify the date is valid before formatting
  if (!date.isValid()) {
    return "";
  }

  const time = formatAppointmentTime(startTime);
  const dayOfWeek = date.format("ddd");
  const month = date.format("MMM");
  const dayOfMonth = date.format("D");

  return ` - ${dayOfWeek} ${month} ${dayOfMonth}, ${time}`;
};

interface LocationSelectionModalProps {
  isOpen: boolean;
  providerName: string;
  providerType?: string; // e.g., "LMSW", "MD", "DO"
  appointmentDate?: string; // ISO format YYYY-MM-DD
  startTime?: string; // HH:MM format
  options: DepartmentOption[];
  initialValue: string;
  onConfirm: (value: string) => void;
  onClose: () => void;
}

export const LocationSelectionModal = ({
  isOpen,
  providerName,
  providerType,
  appointmentDate,
  startTime,
  options,
  initialValue,
  onConfirm,
  onClose,
}: LocationSelectionModalProps) => {
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [sessionLocationError, setSessionLocationError] = useState<string>();
  const { isInSalesforce: canvasIsInSalesforce } = useCanvasContext();

  useCanvasPortalScrollFix(canvasIsInSalesforce);

  useEffect(() => {
    if (isOpen) {
      setSelectedValue(initialValue);
      setSessionLocationError(undefined);
    }
  }, [initialValue, isOpen]);

  const footer = useMemo(() => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: "12px",
          width: "100%",
        }}
      >
        <Button
          data-testid="test-id-find-provider-location-modal-cancel-button"
          id="obc-find-provider-location-modal-cancel-btn"
          data-obc-label="Find Provider Location Modal Cancel Button"
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid="test-id-find-provider-location-modal-confirm-button"
          id="obc-find-provider-location-modal-confirm-btn"
          data-obc-label="Find Provider Location Modal Confirm Button"
          variant="filled"
          onClick={() => {
            if (!selectedValue) {
              setSessionLocationError("Please select a session location");
              return;
            }

            onConfirm(selectedValue);
          }}
        >
          Confirm
        </Button>
      </div>
    );
  }, [onClose, onConfirm, selectedValue]);

  if (!isOpen) {
    return null;
  }

  return (
    <ModalDialog
      isOpen={isOpen}
      size={LAYOUT_CONSTANTS.modalDialogWidth}
      closeOnClickOutside={false}
      header="Select a location"
      footer={footer}
      data-testid="test-id-find-provider-location-selection-modal"
      data-obc-label="Find Provider Location Selection Modal"
      onClose={onClose}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px", paddingBottom: "16px" }}>
        <SlotDetails>
          <ProviderNameContainer>
            <IconSymbol icon="calendar_today" color="$app.color.text.primary" />
            <Heading
              size="xxs"
              data-testid="test-id-find-provider-location-selection-modal-provider-name"
            >
              {providerName}
              {providerType ? `, ${providerType}` : ""}
              {formatSlotDateTime(appointmentDate, startTime)}
            </Heading>
          </ProviderNameContainer>
        </SlotDetails>
        <Text>Please choose a preferred location for the consultation:</Text>
        <SelectInput
          id="obc-find-provider-location-select"
          data-testid="test-id-find-provider-location-select"
          data-obc-label="Find Provider Location Select"
          label="Session Location"
          aria-label="Session Location"
          value={selectedValue}
          options={options}
          onChange={(value) => {
            setSelectedValue(String(value));
            setSessionLocationError(undefined);
          }}
          isRequired={true}
          errorMessage={sessionLocationError}
          enableOutsideScroll={canvasIsInSalesforce}
        />
      </div>
    </ModalDialog>
  );
};
